import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../index.css";
import { getApiUrl, API_CONFIG } from "../config/api";
import { useViewport } from "../hooks/useViewport";
import ViewportPreloader from "./ViewportPreloader";
import { validateUsername, validatePassword, invalidateValidateTokenMemoryCache } from "../utils/uiHandlers";
import { getFirstAllowedHomePathFromModuleAccess } from "../utils/moduleAccess";
import { requestBrowserNotificationPermissionFromGesture } from "../utils/browserNotifications";
import { registerServiceWorkerForNotifications } from "../utils/webPush";
import "bootstrap-icons/font/bootstrap-icons.css";

/** Tras login correcto el clic en "Entrar" permite pedir permiso de notificaciones del SO (Chrome/Edge/Firefox). */
function scheduleDesktopNotificationSetup() {
  requestBrowserNotificationPermissionFromGesture().then((perm) => {
    if (perm === "granted") registerServiceWorkerForNotifications().catch(() => {});
  });
}

// No usar variables locales ni JWT para estado de usuario. must_change_password se lee siempre del backend (tablas).

// Validación de contraseña segura (misma lógica que página de cambio de clave)
const validateSecurePassword = (pwd, currentPwd = '') => {
  const err = [];
  if (!pwd || pwd.length < 8) err.push('Al menos 8 caracteres');
  if (pwd.length > 128) err.push('Máximo 128 caracteres');
  if (/\s/.test(pwd)) err.push('Sin espacios');
  if (!/[A-Z]/.test(pwd)) err.push('Al menos una mayúscula');
  if (!/[a-z]/.test(pwd)) err.push('Al menos una minúscula');
  if (!/[0-9]/.test(pwd)) err.push('Al menos un número');
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) err.push('Al menos un carácter especial');
  if (currentPwd && pwd === currentPwd) err.push('Debe ser distinta a la actual');
  return err;
};

const getPasswordRequirementChecks = (pwd, currentPwd = '') => ({
  minLength: !!pwd && pwd.length >= 8,
  maxLength: !!pwd && pwd.length <= 128,
  noSpaces: !!pwd && !/\s/.test(pwd),
  hasUpper: !!pwd && /[A-Z]/.test(pwd),
  hasLower: !!pwd && /[a-z]/.test(pwd),
  hasNumber: !!pwd && /[0-9]/.test(pwd),
  hasSpecial: !!pwd && /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
  differentFromCurrent: !currentPwd || (pwd && pwd !== currentPwd)
});

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const navigate = useNavigate();
  const viewport = useViewport();
  const passwordChecks = getPasswordRequirementChecks(
    changePasswordData.newPassword,
    changePasswordData.currentPassword
  );
  const passwordsMatch =
    !!changePasswordData.newPassword &&
    !!changePasswordData.confirmPassword &&
    changePasswordData.newPassword === changePasswordData.confirmPassword;
  const confirmTouched = changePasswordData.confirmPassword.length > 0;

  // Establecer título de la página
  useEffect(() => {
    document.title = 'Novaiops';
  }, []);

  // No consultar perfil al abrir /login: una cookie antigua activaba "Cambio obligatorio" sin haber iniciado sesión.
  // El modal solo se muestra tras POST /login exitoso cuando el backend indica must_change_password.

  // Aplicar estilos específicos según el viewport
  useEffect(() => {
    // El hook useViewport ya maneja los ajustes de fuente según el zoom
    // No necesitamos aplicar transform scale que puede causar problemas de layout
  }, [viewport]);

  // Sin efecto de mouse en el fondo para evitar sensación de movimiento de página
  // (el gradiente y la decoración quedan estáticos o solo con animación suave propia)
  useEffect(() => {
    const loginBackground = document.querySelector('.login-background');
    const animatedGradient = document.querySelector('.login-animated-gradient');
    const backgroundDecoration = document.querySelector('.login-background-decoration');
    if (!loginBackground || !animatedGradient || !backgroundDecoration) return;
    // Reset fijo para que no haya parallax ni brillo siguiendo al mouse
    animatedGradient.style.transform = 'translate(0, 0)';
    backgroundDecoration.style.transform = 'translate(0, 0)';
    loginBackground.style.setProperty('--mouse-x', '50%');
    loginBackground.style.setProperty('--mouse-y', '50%');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError("");
    setIsLoading(true);

    // Validar y sanitizar username
    const usernameValidation = validateUsername(username);
    if (!usernameValidation.valid) {
      setError(usernameValidation.error);
      setIsLoading(false);
      return;
    }

    // Validar password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        username: usernameValidation.value,
        password: passwordValidation.value,
      };

      const loginUrl = getApiUrl("LOGIN");
      const res = await axios.post(loginUrl, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
        // El backend hace bcrypt, módulos, sesiones y limpieza; en máquinas lentas o BD ocupada puede superar 10 s.
        // Un timeout corto provoca "Tiempo de espera agotado" aunque el servidor acabe en 200 (carrera con el límite).
        timeout: 60000,
      });

      if (res.data && res.data.result) {
        // El backend establece la cookie auth_token y devuelve el usuario desde la base de datos.
        const token = res.data.result;
        if (!token || typeof token !== 'string' || token.trim() === '') {
          setError("Error: Token de autenticación inválido");
          setIsLoading(false);
          return;
        }
        invalidateValidateTokenMemoryCache();
        // Solo guardar token para Authorization Bearer al microservicio de clientes; estado de usuario siempre desde BD.
        try { sessionStorage.setItem('auth_token', token); } catch (_) {}
        window.dispatchEvent(new Event('realtime:token_set'));
        scheduleDesktopNotificationSetup();
        // must_change_password solo desde la respuesta del backend (tablas), no desde JWT ni variables locales
        const user = res.data.user;
        const mustChange = !!(user && (user.must_change_password === true || user.mustChangePassword === true));
        if (mustChange) {
          setShowChangePassword(true);
          setError("");
        } else {
          const home = getFirstAllowedHomePathFromModuleAccess(user?.module_access);
          if (home) navigate(home);
          else navigate("/");
        }
      } else {
        setError("Credenciales incorrectas");
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setError("Usuario o contraseña incorrectos");
        } else if (err.response.status === 409) {
          setError("Ya tienes una sesión activa. Por favor, cierra sesión en otros dispositivos.");
        } else {
          setError(`Error del servidor: ${err.response.status}`);
        }
      } else if (err.request) {
        const base = API_CONFIG.BASE_URL || "(sin REACT_APP_API_BASE_URL)";
        const code = err.code || "";
        let extra = "";
        if (code === "ECONNABORTED") {
          extra = " Tiempo de espera agotado (60 s). Si persiste, el servidor tarda demasiado (BD o carga).";
        } else if (code === "ERR_NETWORK" || code === "ECONNREFUSED") {
          extra = " No hay respuesta del API; suele indicar que el backend no está en marcha o el puerto/host no coinciden.";
        }
        setError(
          `No se pudo conectar con el servidor en ${base}.${extra} Compruebe que el backend esté ejecutándose (mismo host/puerto que REACT_APP_API_BASE_URL) y que no mezcle http con https en la misma página.`
        );
      } else {
        setError("Error inesperado, intente de nuevo");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangePasswordError("");
    const { currentPassword, newPassword, confirmPassword } = changePasswordData;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setChangePasswordError("Todos los campos son obligatorios.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError("La nueva contraseña y la confirmación no coinciden.");
      return;
    }
    const errs = validateSecurePassword(newPassword, currentPassword);
    if (errs.length) {
      setChangePasswordError("Nueva contraseña: " + errs.join(", "));
      return;
    }
    setChangePasswordLoading(true);
    try {
      const res = await fetch(getApiUrl("USER_CHANGE_PASSWORD"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setChangePasswordError(data.result || "Error al cambiar la contraseña.");
        setChangePasswordLoading(false);
        return;
      }
      setChangePasswordSuccess(true);
      setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      scheduleDesktopNotificationSetup();
      // El backend actualiza la sesión en BD y puede devolver nuevo token; solo guardamos para Bearer al microservicio
      try {
        if (data.new_token) {
          sessionStorage.setItem('auth_token', data.new_token);
          window.dispatchEvent(new Event('realtime:token_set'));
        } else {
          sessionStorage.removeItem('auth_token');
        }
      } catch (_) {}
      setTimeout(() => {
        const home = getFirstAllowedHomePathFromModuleAccess(undefined);
        navigate(home || "/overview");
      }, 2500);
    } catch (err) {
      setChangePasswordError(err.message || "Error de conexión. Intente de nuevo.");
    } finally {
      setChangePasswordLoading(false);
    }
  };

  // Determinar clase del dispositivo para CSS
  const getDeviceClass = () => {
    if (viewport.isSmallMobile) return 'small-mobile';
    if (viewport.isMobile) return 'mobile';
    if (viewport.isTablet) return 'tablet';
    if (viewport.isLargeDesktop) return 'large-desktop';
    return 'desktop';
  };

  return (
    <ViewportPreloader>
      <div className={`login-background d-flex min-vh-100 device-${getDeviceClass()} zoom-${viewport.zoomLevel > 1.3 ? 'high' : viewport.zoomLevel > 1.1 ? 'medium' : 'normal'}`}>
        {/* Gradiente animado de fondo */}
        <div className="login-animated-gradient"></div>
        {/* Elementos decorativos de fondo */}
        <div className="login-background-decoration"></div>
        {/* Partículas flotantes adicionales */}
        <div className="login-particles"></div>

        <div className="login-card">
          <div className="login-card-container">
            {/* Columna izquierda: Branding (estilo imagen) */}
            {!viewport.isSmallMobile && !viewport.isMobile && (
              <div className="login-branding-section">
                <div className="login-branding-shapes">
                  <div className="login-branding-curve login-branding-curve-1" aria-hidden="true"></div>
                  <div className="login-branding-curve login-branding-curve-2" aria-hidden="true"></div>
                  <div className="login-branding-curve login-branding-curve-3" aria-hidden="true"></div>
                </div>
                <div className="login-branding-content">
                  <h1 className="login-branding-title">Bienvenido a Novaiops</h1>
                  <p className="login-branding-desc">Novaiops te ayuda a tener el control, visibilidad y una mejor experiencia en la gestión de tickets, soporte y customer journey.</p>
                  <button type="button" className="login-branding-cta" onClick={() => window.open('#', '_self')}>Saber más</button>
                </div>
                <div className="login-branding-footer-logo" aria-hidden="true">
                  <span className="login-branding-logo-rect login-branding-logo-rect-a"></span>
                  <span className="login-branding-logo-rect login-branding-logo-rect-b"></span>
                  <span className="login-branding-logo-dot"></span>
                </div>
              </div>
            )}
            {/* Columna derecha: Formulario de login */}
            <div className="login-form-section">
              <div className="login-form-inner">
                <div className="login-form-header">
                  <div className="login-form-logo-mini" aria-hidden="true">
                    <span className="login-form-logo-mini-a"></span>
                    <span className="login-form-logo-mini-b"></span>
                    <span className="login-form-logo-mini-dot"></span>
                  </div>
                  <h2 className="login-title">Iniciar sesión</h2>
                  <p className="login-subtitle">Tu panel de administración</p>
                </div>

                <form onSubmit={handleSubmit} noValidate className="login-form">
                      <div className="login-input-wrapper">
                        <label className="login-label">Usuario</label>
                        <div className="login-input-row">
                          <input
                            type="text"
                            className="login-input-new"
                            placeholder="Usuario"
                            autoComplete="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="login-input-wrapper">
                        <label className="login-label">Contraseña</label>
                        <div className="login-input-row">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="login-input-new login-input-password"
                            placeholder="Contraseña"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            className="login-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          >
                            <i className={showPassword ? "bi bi-eye-slash-fill" : "bi bi-eye-fill"}></i>
                          </button>
                        </div>
                      </div>
                      <button type="submit" className="login-submit-btn-new" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Iniciando sesión...
                          </>
                        ) : (
                          "Iniciar sesión"
                        )}
                      </button>
                      {error && (
                        <div className="login-error-alert">
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          {error}
                        </div>
                      )}
                    </form>
              </div>
            </div>
          </div>
        </div>

        {/* Modal de cambio de contraseña obligatorio (primer ingreso). No se puede cerrar sin cambiar. */}
        {showChangePassword && (
          <div className="login-change-password-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="change-password-modal-title" onClick={(e) => e.target === e.currentTarget && e.stopPropagation()}>
            <div className="login-change-password-modal">
              <div className="login-form-inner">
                <h2 id="change-password-modal-title" className="login-title">Cambio de contraseña obligatorio</h2>
                <p className="login-subtitle">Es su primer ingreso. Debe cambiar su contraseña para continuar.</p>
                {changePasswordSuccess ? (
                  <div className="login-success-message" style={{ textAlign: 'center', padding: '1.5rem', color: '#0d6efd' }}>
                    <i className="bi bi-check-circle-fill" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
                    <strong>Contraseña actualizada correctamente.</strong>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>Ya puede usar la aplicación con su nueva contraseña. Guárdela en un lugar seguro.</p>
                    <p style={{ marginTop: '0.25rem', fontSize: '0.85rem', opacity: 0.9 }}>Redirigiendo...</p>
                  </div>
                ) : (
                  <form onSubmit={handleChangePasswordSubmit} noValidate className="login-form">
                    <div className="login-input-wrapper">
                      <label className="login-label">Contraseña actual</label>
                      <div className="login-input-row">
                        <input
                          type="password"
                          className="login-input-new login-input-password"
                          placeholder="Contraseña actual"
                          autoComplete="current-password"
                          value={changePasswordData.currentPassword}
                          onChange={(e) => setChangePasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div className="login-input-wrapper">
                      <label className="login-label">Nueva contraseña</label>
                      <div className="login-input-row">
                        <input
                          type="password"
                          className="login-input-new login-input-password"
                          placeholder="Nueva contraseña"
                          autoComplete="new-password"
                          value={changePasswordData.newPassword}
                          onChange={(e) => setChangePasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div className="login-input-wrapper">
                      <label className="login-label">Confirmar nueva contraseña</label>
                      <div className="login-input-row">
                        <input
                          type="password"
                          className="login-input-new login-input-password"
                          placeholder="Confirmar nueva contraseña"
                          autoComplete="new-password"
                          value={changePasswordData.confirmPassword}
                          onChange={(e) => setChangePasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        marginBottom: '0.75rem',
                        fontSize: '0.85rem',
                        color: confirmTouched ? (passwordsMatch ? '#198754' : '#dc3545') : '#6c757d'
                      }}
                    >
                      {confirmTouched
                        ? (passwordsMatch ? 'Las contraseñas son iguales.' : 'Las contraseñas no coinciden.')
                        : 'Confirma tu nueva contraseña para comparar.'}
                    </div>
                    <div
                      style={{
                        marginBottom: '1rem',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '0.75rem 0.9rem',
                        background: '#f8f9fa'
                      }}
                    >
                      <div style={{ fontSize: '0.82rem', marginBottom: '0.4rem', color: '#495057', fontWeight: 600 }}>
                        Estándar de contraseña:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.79rem', color: '#495057' }}>
                        <li style={{ color: passwordChecks.minLength ? '#198754' : '#dc3545' }}>Mínimo 8 caracteres</li>
                        <li style={{ color: passwordChecks.maxLength ? '#198754' : '#dc3545' }}>Máximo 128 caracteres</li>
                        <li style={{ color: passwordChecks.noSpaces ? '#198754' : '#dc3545' }}>Sin espacios</li>
                        <li style={{ color: passwordChecks.hasUpper ? '#198754' : '#dc3545' }}>Al menos una mayúscula</li>
                        <li style={{ color: passwordChecks.hasLower ? '#198754' : '#dc3545' }}>Al menos una minúscula</li>
                        <li style={{ color: passwordChecks.hasNumber ? '#198754' : '#dc3545' }}>Al menos un número</li>
                        <li style={{ color: passwordChecks.hasSpecial ? '#198754' : '#dc3545' }}>Al menos un carácter especial</li>
                        <li style={{ color: passwordChecks.differentFromCurrent ? '#198754' : '#dc3545' }}>Debe ser distinta a la actual</li>
                      </ul>
                    </div>
                    <button type="submit" className="login-submit-btn-new" disabled={changePasswordLoading}>
                      {changePasswordLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Actualizando...
                        </>
                      ) : (
                        "Cambiar contraseña"
                      )}
                    </button>
                    {changePasswordError && (
                      <div className="login-error-alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {changePasswordError}
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ViewportPreloader>
  );
}